import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import SeoHead, { SITE_URL } from '@/components/feature/SeoHead';
import ThumbImage from '@/components/base/ThumbImage';
import { supabase } from '@/lib/supabase';
import { T } from '@/hooks/useAutoTranslate';

const HERO_IMG = 'https://readdy.ai/api/search-image?query=happy%20Asian%20families%20with%20newborn%20babies%2C%20joyful%20parents%20holding%20infants%2C%20warm%20soft%20champagne%20gold%20tones%2C%20group%20of%20multicultural%20happy%20families%2C%20fertility%20clinic%20success%20stories%2C%20emotional%20happiness%20professional%20photography&width=1600&height=600&seq=cases-hero-002&orientation=landscape';

interface CaseItem {
  id: number;
  title: string;
  couple: string;
  from_location: string;
  baby_count: string;
  cycle: string;
  method: string;
  tag: string;
  content: string;
  img_url: string;
  status: string;
  published_at: string;
}

const FILTER_TAGS = [
  { key: '全部', labelKey: 'cases_cat_all' },
  { key: '单代孕', labelKey: 'cases_cat_surrogacy' },
  { key: '三代试管', labelKey: 'cases_cat_ivf' },
  { key: '试管+代孕', labelKey: 'cases_cat_full' },
];

const tagLabelMap: Record<string, string> = {};
FILTER_TAGS.forEach((t) => { tagLabelMap[t.key] = t.labelKey; });

interface ShareModalProps {
  caseItem: CaseItem;
  onClose: () => void;
}

function ShareModal({ caseItem, onClose }: ShareModalProps) {
  const url = encodeURIComponent(`${window.location.origin}/cases/${caseItem.id}`);
  const title = encodeURIComponent(caseItem.title);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose}></div>
      <div className="relative bg-white rounded-2xl shadow-xl p-6 w-72">
        <h3 className="font-bold text-[#1a3a5c] text-sm mb-1">分享案例</h3>
        <p className="text-gray-400 text-xs mb-5">传播爱与希望，让更多家庭了解我们</p>
        <div className="flex items-center justify-center gap-3 flex-wrap">
          <a href={`https://t.me/share/url?url=${url}&text=${title}`} target="_blank" rel="nofollow noopener noreferrer" className="w-11 h-11 flex items-center justify-center rounded-full bg-[#0088cc] text-white cursor-pointer hover:opacity-90 transition-opacity"><i className="ri-telegram-line text-lg"></i></a>
          <a href={`https://www.facebook.com/sharer/sharer.php?u=${url}`} target="_blank" rel="nofollow noopener noreferrer" className="w-11 h-11 flex items-center justify-center rounded-full bg-[#1877f2] text-white cursor-pointer hover:opacity-90 transition-opacity"><i className="ri-facebook-line text-lg"></i></a>
          <a href={`https://twitter.com/intent/tweet?url=${url}&text=${title}`} target="_blank" rel="nofollow noopener noreferrer" className="w-11 h-11 flex items-center justify-center rounded-full bg-[#1da1f2] text-white cursor-pointer hover:opacity-90 transition-opacity"><i className="ri-twitter-x-line text-lg"></i></a>
          <a href={`https://wa.me/?text=${title}%20${url}`} target="_blank" rel="nofollow noopener noreferrer" className="w-11 h-11 flex items-center justify-center rounded-full bg-[#25d366] text-white cursor-pointer hover:opacity-90 transition-opacity"><i className="ri-whatsapp-line text-lg"></i></a>
          <button className="w-11 h-11 flex items-center justify-center rounded-full bg-[#07c160] text-white cursor-pointer hover:opacity-90 transition-opacity"><i className="ri-wechat-line text-lg"></i></button>
        </div>
        <button onClick={onClose} className="absolute top-3 right-3 w-7 h-7 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer rounded-full hover:bg-gray-100">
          <i className="ri-close-line"></i>
        </button>
      </div>
    </div>
  );
}

const casesJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'CollectionPage',
  name: '好孕案例 - RTB吉尔吉斯斯坦试管代孕中心',
  description: '500+成功代孕案例展示，来自中国、俄罗斯、哈萨克斯坦等国家的真实家庭好孕故事，试管婴儿、代孕、捐卵成功案例。',
  url: `${SITE_URL}/cases`,
};

export default function CasesPage() {
  const { t } = useTranslation();
  const [activeTag, setActiveTag] = useState('全部');
  const [shareItem, setShareItem] = useState<CaseItem | null>(null);
  const [cases, setCases] = useState<CaseItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchCases = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      let query = supabase.from('success_cases').select('*').eq('status', 'published').order('sort_order', { ascending: true }).order('published_at', { ascending: false });
      if (activeTag !== '全部') query = query.eq('tag', activeTag);
      const { data, error: supaErr } = await query;
      if (supaErr) throw supaErr;
      setCases(data ?? []);
    } catch (err: any) {
      setError(err?.message || '加载失败，请重试');
    } finally {
      setLoading(false);
    }
  }, [activeTag]);

  useEffect(() => { fetchCases(); }, [fetchCases]);

  return (
    <>
      <SeoHead
        title="好孕案例｜500+真实成功故事 - RTB吉尔吉斯斯坦试管代孕中心"
        description="RTB吉尔吉斯斯坦试管代孕中心500+真实成功案例，涵盖试管婴儿、代孕、捐卵、高龄求子等多种方案，98%委托方满意度，见证每个家庭圆梦时刻。"
        keywords="代孕成功案例,试管婴儿案例,吉尔吉斯斯坦代孕案例,好孕故事"
        canonical="/cases"
        jsonLd={casesJsonLd}
      />
      <main className="pt-16 md:pt-20">
        {/* Hero */}
        <section className="relative h-56 md:h-80 flex items-center overflow-hidden">
          <img src={HERO_IMG} alt="好孕案例" className="absolute inset-0 w-full h-full object-cover object-top" />
          <div className="absolute inset-0 bg-[#0d2137]/65"></div>
          <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 w-full">
            <div className="text-[#c9a96e] text-sm font-medium tracking-widest uppercase mb-2">{t('cases_section_label')}</div>
            <h1 className="text-3xl md:text-5xl font-bold text-white">{t('cases_title')}</h1>
            <p className="text-white/70 text-sm mt-3">{t('cases_subtitle')}</p>
          </div>
        </section>

        {/* Stats bar */}
        <section className="bg-[#1a3a5c] py-6">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
              {[
                { num: '500+', labelKey: 'cases_stats_stories' },
                { num: '15+', labelKey: 'cases_stats_countries' },
                { num: '98%', labelKey: 'cases_stats_satisfaction' },
                { num: '10年+', labelKey: 'cases_stats_years' },
              ].map((s) => (
                <div key={s.labelKey}>
                  <div className="text-xl md:text-2xl font-bold text-[#c9a96e]">{s.num}</div>
                  <div className="text-white/60 text-xs mt-0.5">{t(s.labelKey)}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Filter */}
        <section className="py-6 bg-[#f8f5f0] border-b border-gray-100">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="flex flex-wrap gap-2">
              {FILTER_TAGS.map((tag) => (
                <button
                  key={tag.key}
                  onClick={() => setActiveTag(tag.key)}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                    activeTag === tag.key ? 'bg-[#1a3a5c] text-white' : 'bg-white text-gray-500 border border-gray-200 hover:border-[#c9a96e]'
                  }`}
                >
                  {t(tag.labelKey)}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Cases grid */}
        <section className="py-12 md:py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <div key={i} className="bg-[#f8f5f0] rounded-2xl h-80 animate-pulse"></div>
                ))}
              </div>
            ) : error ? (
              <div className="text-center py-20">
                <i className="ri-error-warning-line text-3xl text-red-300 mb-3 block"></i>
                <p className="text-red-500 text-sm mb-3">{error}</p>
                <button onClick={fetchCases} className="text-[#c9a96e] text-sm cursor-pointer">{t('retry')}</button>
              </div>
            ) : cases.length === 0 ? (
              <div className="text-center py-20 text-gray-400 text-sm">{t('cases_no_data')}</div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {cases.map((c) => (
                  <div key={c.id} className="group bg-[#f8f5f0] rounded-2xl overflow-hidden hover:-translate-y-1 transition-all duration-300">
                    <div className="w-full h-52 overflow-hidden relative">
                      <ThumbImage src={c.img_url} alt={c.title} title={`${c.title} - 好孕案例`} fallbackSrc="https://readdy.ai/api/search-image?query=happy%20Asian%20couple%20with%20newborn%20baby%20warm%20champagne%20light%20emotional&width=600&height=400&seq=case-ph-01&orientation=landscape" className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                      <div className="absolute top-3 left-3">
                        <span className="inline-block bg-[#1a3a5c]/80 text-[#c9a96e] text-xs font-medium px-3 py-1 rounded-full backdrop-blur-sm"><T text={c.tag} /></span>
                      </div>
                    </div>
                    <div className="p-5">
                      <div className="flex items-center justify-between mb-3 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          {c.couple && <><i className="ri-parent-line text-[#c9a96e]"></i>{c.couple}</>}
                        </span>
                        <span className="flex items-center gap-1">
                          {c.baby_count && <><i className="ri-heart-line text-[#c9a96e]"></i>{c.baby_count}</>}
                        </span>
                        <span className="flex items-center gap-1">
                          {c.from_location && <><i className="ri-map-pin-line text-[#c9a96e]"></i>{c.from_location}</>}
                        </span>
                      </div>
                      <h3 className="font-bold text-[#1a3a5c] text-sm leading-snug mb-2"><T text={c.title} /></h3>
                      <p className="text-gray-500 text-xs leading-relaxed line-clamp-3 mb-4"><T text={c.content} /></p>
                      <div className="flex items-center justify-between pt-3 border-t border-gray-200">
                        <span className="text-gray-400 text-xs">
                          {c.published_at ? new Date(c.published_at).toLocaleDateString('zh-CN') : ''}
                        </span>
                        <button onClick={() => setShareItem(c)} className="flex items-center gap-1 text-gray-400 hover:text-[#c9a96e] text-xs transition-colors cursor-pointer">
                          <i className="ri-share-line"></i>{t('cases_share')}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* CTA */}
        <section className="py-14 bg-[#f8f5f0]">
          <div className="max-w-2xl mx-auto text-center px-4">
            <h2 className="text-2xl font-bold text-[#1a3a5c] mb-3">{t('cases_cta_title')}</h2>
            <p className="text-gray-500 text-sm mb-7">{t('cases_cta_desc')}</p>
            <Link to="/contact" className="inline-flex items-center gap-2 bg-[#c9a96e] hover:bg-[#b8944f] text-white font-semibold px-8 py-3.5 rounded-full transition-colors whitespace-nowrap cursor-pointer">
              <i className="ri-calendar-check-line"></i>{t('hero_cta1')}
            </Link>
          </div>
        </section>

        {shareItem && <ShareModal caseItem={shareItem} onClose={() => setShareItem(null)} />}
      </main>
    </>
  );
}